import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import {
  createTheme,
  useMediaQuery,
  alpha,
  getContrastRatio,
  CssBaseline,
  ThemeProvider,
  Container,
  Typography,
} from '@mui/material';
import { deepmerge } from '@mui/utils';
import {
  getForm
} from '../actions/Form';
import { invertColor, contrastText, changeColor } from '../util/General';
import ReturningSession from '../components/ReturningSession';
import Autosave from '../components/Autosave';
import Form from '../components/Form';

const defaultTheme = {
 components: {
   MuiFormControl: {
     styleOverrides: {
       root: {
         margin: '0.8em 0',
       },
     }
   },
   MuiTextField: {
     defaultProps: {
       variant: 'standard',
     },
   },
   MuiSelect: {
     defaultProps: {
       variant: 'standard',
     },
   },
   MuiStepper: {
     styleOverrides: {
       root: {
         margin: '2em 0 3em 0',
       },
     }
   },
   MuiTypography: {
     styleOverrides: {
       root: {
         margin: '0.5em 0',
       },
     }
   },
 },
 palette: {
   mode: 'light',
   primary: {
     main: '#9E9E9E',
   },
   secondary: {
     main: '#f50057',
   },
 },
};

const Main = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const formId = searchParams?.get('id');
  const sessionIdParam = searchParams?.get('session_id');
  const dispatch = useDispatch();
  const sessionIdParamPart = sessionIdParam?.split(":")?.[1];
  const [sessionId, setSessionId] = useState(sessionIdParamPart ?? uuidv4());
  const [theme, setTheme] = useState(defaultTheme);

  const { formData, loading } = useSelector((state) => state.form);
  const {
    emailVerified,
    mobileVerified,
    returningSession,
    responseData,
  } = useSelector((state) => state.response);

  const mainStyle = {
    margin: 'auto',
    padding: '1rem',
    width: '50%',
  };

  useEffect(() => {
    dispatch(getForm(formId));
  }, []);

  useEffect(() => {
    let cssString = '';
    if (formData?.theme?.css) {
      cssString += formData?.theme?.css
    }
    // console.log('cssString', cssString)
    let newTheme = {}
    if (cssString) {
      newTheme.components = {
        MuiCssBaseline: {
          styleOverrides: cssString,
        },
      }
    }
    newTheme.typography = {
      h1: {
        fontFamily: formData?.theme?.h1?.font ?? formData?.theme?.header?.font ?? 'Arial',
        fontSize: formData?.theme?.h1?.size ?? formData?.theme?.header?.size ?? '12px',
      },
      h2: {
        fontFamily: formData?.theme?.h2?.font ?? formData?.theme?.header?.font ?? 'Arial',
        fontSize: formData?.theme?.h2?.size ?? formData?.theme?.header?.size ?? '12px',
      },
      h3: {
        fontFamily: formData?.theme?.h3?.font ?? formData?.theme?.header?.font ?? 'Arial',
        fontSize: formData?.theme?.h3?.size ?? formData?.theme?.header?.size ?? '12px',
      },
      h4: {
        fontFamily: formData?.theme?.h4?.font ?? formData?.theme?.header?.font ?? 'Arial',
        fontSize: formData?.theme?.h4?.size ?? formData?.theme?.header?.size ?? '12px',
      },
      h5: {
        fontFamily: formData?.theme?.h5?.font ?? formData?.theme?.header?.font ?? 'Arial',
        fontSize: formData?.theme?.h5?.size ?? formData?.theme?.header?.size ?? '12px',
      },
      h6: {
        fontFamily: formData?.theme?.question?.font ?? formData?.theme?.h6?.font ?? formData?.theme?.header?.font ?? 'Arial',
        fontSize: formData?.theme?.question?.size ?? formData?.theme?.h6?.size ?? formData?.theme?.header?.size ?? '12px',
      },
      body1: {
        fontFamily: formData?.theme?.text?.font ?? 'Arial',
        fontSize: formData?.theme?.text?.size ?? '12px',
      },
      body2: {
        fontFamily: formData?.theme?.text?.font ?? 'Arial',
        fontSize: formData?.theme?.text?.size ?? '12px',
      },
      button: {
        fontFamily: formData?.theme?.text?.font ?? 'Arial',
        fontSize: formData?.theme?.text?.size ?? '12px',
      },
      caption: {
        fontFamily: formData?.theme?.question?.font ?? 'Arial',
        fontSize: formData?.theme?.question?.size ?? '12px',
        fontWeight: formData?.theme?.question?.formats?.includes('bold') ? 'bold' : undefined,
        fontStyle: formData?.theme?.question?.formats?.includes('italic') ? 'italic' : undefined,
        whiteSpace: 'normal !important',
      }
    }
    // Note: Set Bold or Italic, H6 is used in Array so we use Question Settings instead of H6
    let themeTexts = newTheme.typography ? Object.keys(newTheme.typography) : []
    if (themeTexts?.length > 0) {
      for (let key of themeTexts) {
        if ((key !== 'h6' && formData?.theme?.[key]?.formats?.includes('bold')) || (key === 'h6' && formData?.theme?.question?.formats?.includes('bold'))) {
          newTheme.typography[key].fontWeight = 'bold'
        }
        if ((key !== 'h6' && formData?.theme?.[key]?.formats?.includes('italic')) || (key === 'h6' && formData?.theme?.question?.formats?.includes('italic'))) {
          newTheme.typography[key].fontStyle = 'italic'
        }
      }
    }

    newTheme.components = {
      ...newTheme.components,
      MuiInputBase: {
        styleOverrides: {
          root: {
            fontFamily: formData?.theme?.text?.font ?? 'Arial',
            fontSize: formData?.theme?.text?.size ?? '12px',
          }
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontFamily: formData?.theme?.question?.font ?? 'Arial',
            fontSize: formData?.theme?.question?.size ?? '12px',
            fontWeight: formData?.theme?.question?.formats?.includes('bold') ? 'bold' : undefined,
            fontStyle: formData?.theme?.question?.formats?.includes('italic') ? 'italic' : undefined,
          }
        }
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            fontFamily: formData?.theme?.question?.font ?? 'Arial',
            fontSize: formData?.theme?.question?.size ?? '12px',
            fontWeight: formData?.theme?.question?.formats?.includes('bold') ? 'bold' : undefined,
            fontStyle: formData?.theme?.question?.formats?.includes('italic') ? 'italic' : undefined,
            whiteSpace: 'normal !important',
            position: 'static !important',
            marginBottom: '8px !important',
            transform: 'none !important',
          }
        }
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            fontFamily: formData?.theme?.question?.font ?? 'Arial',
            backgroundColor: formData?.theme?.color ?? undefined,
          },
        },
      },
      MuiFormControl: {
        styleOverrides: {
          root: {
            display: 'flex !important',
            flexDirection: 'column !important',
          },
        },
      },
      // Explicitly set the font size for MuiFormHelperText text to keep it default
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            fontFamily: formData?.theme?.question?.font ?? 'Arial',
            fontSize: '0.75rem',
            fontWeight: 'normal',
            fontStyle: 'normal',
          },
        },
      },
      // Explicitly set the font size for MuiStepIcon text to keep it default
      MuiStepIcon: {
        styleOverrides: {
          text: {
            // The default font size for 'caption' is typically 0.75rem (12px)
            fontFamily: formData?.theme?.question?.font ?? 'Arial',
            fontSize: '0.75rem',
            fontWeight: 'normal',
            fontStyle: 'normal',
          },
        },
      },
    }
    if (formData?.theme?.color) {
      let mainColor = formData?.theme?.color === '#ffffff' ?
        '#3f51b5' :
        formData?.theme?.color;
      let secondaryColor = formData?.theme?.color === '#ffffff' ?
        '#f50057' :
        invertColor(formData?.theme?.color);
      let errorColor = formData?.theme?.error || '#d32f2f'
      newTheme.palette = {
        mode: 'light',
        primary: {
          light: changeColor(mainColor, 10),
          main: mainColor,
          dark: changeColor(mainColor, -10),
          contrastText: contrastText(mainColor),
        },
        secondary: {
          light: changeColor(secondaryColor, 10),
          main: secondaryColor,
          dark: changeColor(secondaryColor, -10),
          contrastText: contrastText(secondaryColor),
        },
        error: {
          light: changeColor(errorColor, 10),
          main: errorColor,
          dark: changeColor(errorColor, -10),
          contrastText: contrastText(errorColor),
        },
      }
    }
    if (formData?.theme?.button?.background) {
      // console.log('myButtonColor', formData?.theme?.button?.background)
      let buttonBase = formData?.theme?.button?.background
      let buttonMain = buttonBase
      newTheme.palette['myButtonColor'] = {
        main: buttonMain,
        light: changeColor(buttonMain, 10),
        dark: changeColor(buttonMain, -10),
        contrastText: getContrastRatio(buttonMain, '#ffffff') > 3.1 ? '#ffffff' : '#111111',
      }
    }
    if (formData?.theme?.button?.color) {
      // console.log('myButtonTextColor', formData?.theme?.button?.color)
      let buttonBase = formData?.theme?.button?.color
      let buttonMain = buttonBase
      newTheme.palette['myButtonTextColor'] = {
        main: buttonMain,
        light: changeColor(buttonMain, 10),
        dark: changeColor(buttonMain, -10),
        contrastText: getContrastRatio(buttonMain, '#ffffff') > 3.1 ? '#ffffff' : '#111111',
      }
    }
    if (newTheme) {
      setTheme(deepmerge(theme, newTheme))
    }
  }, [formData]);

  const matches = useMediaQuery('(min-width:600px)');

  return (
    <ThemeProvider theme={(current) => {
      const finalTheme = deepmerge(current, theme);
      return createTheme(finalTheme);
    }}>
      <CssBaseline />
      <Container maxWidth={matches ? "md" : false}>
        { sessionIdParam && returningSession && !responseData?._id ?
          <ReturningSession
            formId={formId}
            sessionId={sessionId}
            sessionIdFromParam={sessionIdParam}
          />
          :
          formData?.autosave?.enable && !responseData?._id ?
          <Autosave
            formId={formId}
            sessionId={sessionId}
            sessionIdFromParam={sessionIdParam}
          />
          :
          <Form
            formId={formId}
            sessionId={sessionId}
            sessionIdFromParam={sessionIdParam}
          />
        }
      </Container>
    </ThemeProvider>
  );
};

export default Main;
